import { Flex } from "@otrium/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DropdownOutlined } from "src/atoms/DropDown";
import { FilterCountBadge } from "src/atoms/FilterCountBadge";
import NoSSR from "src/atoms/NoSSR";
import { debounce } from "src/utils";
import { PriceInputs, PriceRange } from "../Range";
import { ResetFilters } from "src/organisms/ProductList/components/ResetFilters";
import { useSegment } from "src/hooks/useSegment";
import { useFacetContext } from "src/organisms/ProductList/components/Facets/facet.context";
import type { ListingFacet } from "src/types/adapters/listing.type";

type RangeBoundaries = [number, number];

type RangeProps = {
  facet: ListingFacet;
  placeholder: string;
};

function Range({ facet, placeholder }: RangeProps) {
  const [isOpen, setOpen] = useState(false);
  const { segmentProductListFilterOpened } = useSegment();
  const { setRange, clearKey } = useFacetContext();

  const bounds = useMemo(() => {
    if (facet.type !== "range") {
      return {
        min: 0,
        max: 0,
        currentMin: 0,
        currentMax: 0,
        hasSelection: false,
      };
    }

    const { min, max, currentValue } = facet.data;

    return {
      min,
      max,
      currentMin: currentValue?.min ?? min,
      currentMax: currentValue?.max ?? max,
      hasSelection: !!currentValue,
    };
  }, [facet.type, facet.data]);

  const [rangeValue, setRangeValue] = useState<RangeBoundaries>([
    bounds.currentMin,
    bounds.currentMax,
  ]);

  useEffect(() => {
    setRangeValue([bounds.currentMin, bounds.currentMax]);
  }, [bounds.currentMin, bounds.currentMax]);

  const activeCount = bounds.hasSelection ? 1 : 0;

  const triggerRange = useCallback(
    ([rangeMin, rangeMax]: RangeBoundaries) => {
      if (rangeMin === bounds.currentMin && rangeMax === bounds.currentMax) {
        return;
      }

      if (rangeMin === bounds.min && rangeMax === bounds.max) {
        clearKey(facet.key);
        return;
      }

      setRange(facet.key, rangeMin, rangeMax);
    },
    [
      bounds.min,
      bounds.max,
      bounds.currentMin,
      bounds.currentMax,
      facet.key,
      setRange,
      clearKey,
    ]
  );

  const toggleOpen = debounce(() => {
    setOpen(!isOpen);

    if (!isOpen) {
      void segmentProductListFilterOpened(facet.key);
    }
  }, 100);

  const closeDropdown = useCallback(() => {
    triggerRange(rangeValue);
    setOpen(false);
  }, [triggerRange, rangeValue]);

  const handleRefine = useCallback(() => {
    triggerRange(rangeValue);
  }, [triggerRange, rangeValue]);

  const handleChange = useCallback(
    ([rangeMin, rangeMax]: number[]) =>
      setRangeValue([rangeMin, rangeMax] as RangeBoundaries),
    []
  );

  const handleResetFilters = useCallback(() => {
    clearKey(facet.key);
    setRangeValue([bounds.min, bounds.max]);
    setOpen(false);
  }, [bounds.min, bounds.max, facet.key, clearKey]);

  if (facet.type !== "range") {
    return null;
  }

  return (
    <DropdownOutlined
      opened={isOpen}
      hasActiveFilters={activeCount > 0}
      onClose={closeDropdown}
    >
      <DropdownOutlined.Button onClick={toggleOpen}>
        <span>
          {placeholder}
          <FilterCountBadge count={activeCount > 0 ? 1 : 0} />
        </span>
      </DropdownOutlined.Button>
      {isOpen ? (
        <DropdownOutlined.Items>
          <Flex flexDirection="column" maxHeight="inherit">
            <NoSSR>
              <Flex mb={2} alignItems="center">
                <PriceInputs
                  range={[String(rangeValue[0]), String(rangeValue[1])]}
                  onInputChange={(index) => (e) => {
                    const newRangeValue: RangeBoundaries = [...rangeValue];
                    newRangeValue[index] = Number(e.currentTarget.value);
                    handleChange(newRangeValue as number[]);
                  }}
                />
              </Flex>
            </NoSSR>
            <PriceRange
              range={[String(rangeValue[0]), String(rangeValue[1])]}
              onSliderChange={handleChange}
              handleRange={handleRefine}
              initialMinPrice={bounds.min}
              initialMaxPrice={bounds.max}
            />

            <ResetFilters
              activeCount={activeCount}
              onClear={handleResetFilters}
            />
          </Flex>
        </DropdownOutlined.Items>
      ) : null}
    </DropdownOutlined>
  );
}

export { Range };
